// static/table_engine_src/core/clipboard.js

// Basic CSV/TSV parser with quotes (works for Excel paste)
function parseDelimited(text) {
  const src = String(text ?? "");
  const delimiter = src.includes("\t") ? "\t" : ",";

  const rows = [];
  let row = [];
  let field = "";
  let inQuotes = false;

  for (let i = 0; i < src.length; i++) {
    const ch = src[i];

    if (inQuotes) {
      if (ch === '"') {
        if (src[i + 1] === '"') {
          field += '"';
          i++;
        } else {
          inQuotes = false;
        }
      } else {
        field += ch;
      }
      continue;
    }

    if (ch === '"') {
      inQuotes = true;
      continue;
    }

    if (ch === delimiter) {
      row.push(field);
      field = "";
      continue;
    }

    if (ch === "\r") continue;

    if (ch === "\n") {
      row.push(field);
      field = "";
      rows.push(row);
      row = [];
      continue;
    }

    field += ch;
  }

  row.push(field);
  rows.push(row);

  while (rows.length && rows[rows.length - 1].every((x) => String(x).trim() === "")) rows.pop();
  return rows;
}

function escapeHtml(value) {
  return String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

function gridToTsv(grid) {
  const rows = Array.isArray(grid) ? grid : [];
  return rows
    .map((row) => (Array.isArray(row) ? row : [row]).map((cell) => String(cell ?? "")).join("\t"))
    .join("\n");
}

function gridToHtmlTable(grid) {
  const rows = Array.isArray(grid) ? grid : [];
  const body = rows
    .map((row) => {
      const cells = (Array.isArray(row) ? row : [row])
        .map((cell) => `<td>${escapeHtml(cell)}</td>`)
        .join("");
      return `<tr>${cells}</tr>`;
    })
    .join("");
  return `<table><tbody>${body}</tbody></table>`;
}

function htmlTableToGrid(html) {
  const src = String(html ?? "").trim();
  if (!src) return [];

  if (typeof DOMParser !== "function") return parseDelimited(src);

  let doc = null;
  try {
    doc = new DOMParser().parseFromString(src, "text/html");
  } catch {
    return parseDelimited(src);
  }
  if (!doc) return parseDelimited(src);

  const table = doc.querySelector("table");
  if (!table) {
    const txt = String(doc.body?.textContent ?? "").trim();
    return parseDelimited(txt);
  }

  const rows = [];
  const trEls = table.querySelectorAll("tr");
  trEls.forEach((tr) => {
    const cells = [];
    tr.querySelectorAll("th,td").forEach((cell) => {
      cells.push(String(cell.textContent ?? "").replace(/\u00a0/g, " ").trim());
    });
    rows.push(cells);
  });

  while (rows.length && rows[rows.length - 1].every((x) => String(x).trim() === "")) rows.pop();
  return rows;
}

async function clipboardReadData() {
  const out = { text: "", html: "" };

  try {
    if (navigator.clipboard?.read) {
      const items = await navigator.clipboard.read();
      for (const item of items || []) {
        try {
          if (!out.html && item.types?.includes?.("text/html")) {
            const blob = await item.getType("text/html");
            out.html = String(await blob.text());
          }
        } catch {}
        try {
          if (!out.text && item.types?.includes?.("text/plain")) {
            const blob = await item.getType("text/plain");
            out.text = String(await blob.text());
          }
        } catch {}
        if (out.text && out.html) break;
      }
      if (out.text || out.html) return out;
    }
  } catch {
    // fall through
  }

  try {
    if (navigator.clipboard?.readText) out.text = String(await navigator.clipboard.readText());
  } catch {}
  return out;
}

async function clipboardReadText() {
  const data = await clipboardReadData();
  if (data.text) return data.text;
  if (data.html) return gridToTsv(htmlTableToGrid(data.html));
  return "";
}

function execCommandCopyFallback(text) {
  const ta = document.createElement("textarea");
  ta.value = String(text ?? "");
  ta.setAttribute("readonly", "true");
  ta.style.position = "fixed";
  ta.style.left = "-9999px";
  ta.style.top = "-9999px";
  ta.style.opacity = "0";
  document.body.appendChild(ta);

  // iOS/Safari friendliness
  try {
    ta.focus({ preventScroll: true });
  } catch {
    ta.focus();
  }
  try {
    ta.select();
  } catch {}

  let ok = false;
  try {
    ok = document.execCommand("copy");
  } catch {
    ok = false;
  }

  ta.remove();
  return ok;
}

async function clipboardWriteText(text) {
  return clipboardWriteData({ text: String(text ?? "") });
}

async function clipboardWriteData(payload) {
  const text = String(payload?.text ?? "");
  const html = String(payload?.html ?? "");

  if (navigator.clipboard?.write && typeof ClipboardItem !== "undefined") {
    try {
      const itemData = {};
      if (text) itemData["text/plain"] = new Blob([text], { type: "text/plain" });
      if (html) itemData["text/html"] = new Blob([html], { type: "text/html" });
      if (Object.keys(itemData).length) {
        await navigator.clipboard.write([new ClipboardItem(itemData)]);
        return true;
      }
    } catch {
      // fall through
    }
  }

  if (navigator.clipboard?.writeText) {
    try {
      await navigator.clipboard.writeText(text || (html ? gridToTsv(htmlTableToGrid(html)) : ""));
      return true;
    } catch {
      // fall through to execCommand fallback
    }
  }

  return execCommandCopyFallback(text || (html ? gridToTsv(htmlTableToGrid(html)) : ""));
}

export {
  parseDelimited,
  gridToTsv,
  gridToHtmlTable,
  htmlTableToGrid,
  clipboardReadData,
  clipboardReadText,
  clipboardWriteData,
  clipboardWriteText,
};
